import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../../../../../../hooks/useHandleAppException';
import { useFindOrderConditionsCustomFields } from '../../../../../../../../../services/modules/orderCondition/orderCondition.queries';
import snackbarStore from '../../../../../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../../../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../../../../../../context/loadingStatus/LoadingStatus.store';
import useFilterOrderConditionCustomFields from '../../../../../../../conditions/components/customDefinitions/components/customFields/hooks/useFilterOrderConditionCustomFields';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import OrderConditionCustomFieldConfig from '../../../../../../../conditions/interfaces/orderConditionCustomFieldConfig.interface';
import {
  onlineOrderConditionRepository,
  orderConditionRepository,
} from '../../../../../../../conditions/orderCondition.repository';
import { useOrderCalculateContext } from '../../../../../../../contexts/OrderCalculate.context';
import { useOrderConditionContext } from '../../../../../../../contexts/OrderCondition.context';
import { validateOrderConditionsQuery } from '../../../../../../../contexts/order.utils';
import {
  Order,
  OrderBatchItemsType,
  OrderOperationsIntegrationType,
} from '../../../../../../../order.types';
import { OrderConditionApplyToAllState } from '../../OrderConditionApplyToAllModal';
import {
  OrderConditionDefinitionsFormValues,
  orderConditionDefinitionsSchema,
} from '../orderConditionDefinitionsModal.schema';
import { buildOrderConditionUpdateDto } from '../orderConditionDefinitionsModal.utils';

interface UseOrderConditionDefinitionsFormParams {
  order: Order;
  condition: OrderCondition;
  isOpen: boolean;
  onClose: () => void;
}

interface UseOrderConditionDefinitionsFormResult {
  formMethods: UseFormReturn<OrderConditionDefinitionsFormValues>;
  isSaving: boolean;
  customFields: OrderConditionCustomFieldConfig[];
  customFieldValues: Record<string, unknown>;
  selectedOrderOperation: OrderOperationsIntegrationType | null;
  selectedOrderBatchItem: OrderBatchItemsType | null;
  setCustomFieldValue: (configId: string, value: unknown) => void;
  setSelectedOrderOperation: (value: OrderOperationsIntegrationType | null) => void;
  setSelectedOrderBatchItem: (value: OrderBatchItemsType | null) => void;
  save: () => Promise<void>;
  applyToAll: OrderConditionApplyToAllState;
}

const buildDefaultValues = (condition: OrderCondition): OrderConditionDefinitionsFormValues => ({
  orderCollectionId: condition.collection?.id ?? null,
  saleTypeId: condition.saleType?.id ?? null,
  branchId: condition.local?.id ?? null,
  freightType: condition.freightType ?? null,
  shippingCompanyId: condition.shippingCompany?.id ?? null,
  brandFreightTypeId: condition.brandFreightType?.id ?? null,
  orderOperationCode:
    condition.orderOperationCode != null ? String(condition.orderOperationCode) : null,
  orderChargeTypeCode:
    condition.orderChargeTypeCode != null ? String(condition.orderChargeTypeCode) : null,
  orderBatchItemCode: condition.orderBatchItemCode ?? null,
  orderClassificationTypeCode: condition.orderClassifications?.[0]?.typeCode ?? null,
  orderClassificationCode: condition.orderClassifications?.[0]?.code ?? null,
  emitInvoice: condition.emitInvoice ?? false,
});

const buildInitialOrderOperation = (
  condition: OrderCondition,
): OrderOperationsIntegrationType | null =>
  condition.orderOperationCode != null
    ? { code: condition.orderOperationCode, name: String(condition.orderOperationCode) }
    : null;

const buildInitialOrderBatchItem = (condition: OrderCondition): OrderBatchItemsType | null =>
  condition.orderBatchItemCode
    ? { code: condition.orderBatchItemCode, name: condition.orderBatchItemCode }
    : null;

const buildInitialCustomFieldValues = (condition: OrderCondition): Record<string, unknown> =>
  Object.fromEntries((condition.customFields ?? []).map((cf) => [cf.configId, cf.value]));

const useOrderConditionDefinitionsForm = ({
  order,
  condition,
  isOpen,
  onClose,
}: UseOrderConditionDefinitionsFormParams): UseOrderConditionDefinitionsFormResult => {
  const queryClient = useQueryClient();
  const { handleErrorResponse } = useHandleAppException();
  const { team } = useApplicationContext();
  const { orderConditions, invalidateOrderConditions } = useOrderConditionContext();
  const { calculateSummary } = useOrderCalculateContext();
  const { handleMessage } = snackbarStore;
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>(
    buildInitialCustomFieldValues(condition),
  );
  const [selectedOrderOperation, setSelectedOrderOperation] =
    useState<OrderOperationsIntegrationType | null>(buildInitialOrderOperation(condition));
  const [selectedOrderBatchItem, setSelectedOrderBatchItem] = useState<OrderBatchItemsType | null>(
    buildInitialOrderBatchItem(condition),
  );

  const { data: customFieldConfigs = [] } = useFindOrderConditionsCustomFields();
  const { filterCustomFieldsByOrderConditionAndUserRole } = useFilterOrderConditionCustomFields();
  const customFields = filterCustomFieldsByOrderConditionAndUserRole({
    userRole: team?.role,
    orderCondition: condition,
    orderConditionCustomFields: customFieldConfigs,
  });

  const formMethods = useForm<OrderConditionDefinitionsFormValues>({
    resolver: zodResolver(orderConditionDefinitionsSchema),
    defaultValues: buildDefaultValues(condition),
  });

  useEffect(() => {
    if (isOpen) {
      formMethods.reset(buildDefaultValues(condition));
      setCustomFieldValues(buildInitialCustomFieldValues(condition));
      setSelectedOrderOperation(buildInitialOrderOperation(condition));
      setSelectedOrderBatchItem(buildInitialOrderBatchItem(condition));
    }
  }, [isOpen, condition.id]);

  const [isApplyToAllOpen, setIsApplyToAllOpen] = useState(false);

  const setCustomFieldValue = (configId: string, value: unknown) => {
    setCustomFieldValues((current) => ({ ...current, [configId]: value }));
  };

  const mutation = useMutation({
    scope: { id: `order-conditions-${order.id}` },
    mutationFn: (shouldApplyToAll: boolean) => {
      const values = formMethods.getValues();

      const dto = buildOrderConditionUpdateDto({
        conditionId: condition.id,
        values,
        selectedOrderOperation,
        selectedOrderBatchItem,
        customFields,
        customFieldValues,
      });

      const requestFn = () =>
        shouldApplyToAll
          ? onlineOrderConditionRepository(order.id).updateAll({
              ...dto,
              orderConditionsIds: orderConditions.map((item) => item.id),
            })
          : orderConditionRepository(order.id, condition.id).update(dto);

      return handleLoadingStatus({ requestFn });
    },
    onSuccess: async () => {
      invalidateOrderConditions();
      await calculateSummary();
      await queryClient.refetchQueries(validateOrderConditionsQuery(order.id, team?.id));

      onClose();
    },
    onError: (error: Error) => {
      try {
        const message = handleErrorResponse({
          data: JSON.parse(error.message),
          ok: false,
        } as unknown as AppExceptionResponse);

        handleMessage({ message, type: 'error' });
      } catch {
        handleMessage({ message: error.message, type: 'error' });
      }
    },
  });

  const save = async () => {
    const isValid = await formMethods.trigger();

    if (!isValid) {
      return;
    }

    if (orderConditions.length > 1) {
      setIsApplyToAllOpen(true);
      return;
    }

    mutation.mutate(false);
  };

  const applyToScope = (shouldApplyToAll: boolean) => {
    setIsApplyToAllOpen(false);
    mutation.mutate(shouldApplyToAll);
  };

  return {
    formMethods,
    isSaving: mutation.isPending,
    applyToAll: {
      isOpen: isApplyToAllOpen,
      isSaving: mutation.isPending,
      onApplyToCurrent: () => applyToScope(false),
      onApplyToAll: () => applyToScope(true),
    },
    customFields,
    customFieldValues,
    selectedOrderOperation,
    selectedOrderBatchItem,
    setCustomFieldValue,
    setSelectedOrderOperation,
    setSelectedOrderBatchItem,
    save,
  };
};

export default useOrderConditionDefinitionsForm;
